import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info, RefreshCw, X } from 'lucide-react';
import { formatInfraDateTime, formatInfraDurationHours } from '../utils/infraAnalytics';
import osMetricsService from '../services/osMetricsService';

const TITLE_ID = 'os-sla-detalhe-title';

const SITUACAO_LABELS = {
  dentro: 'Dentro do prazo',
  atrasadas: 'Atrasadas',
  sem_prazo: 'Sem prazo definido',
};

const ORIGEM_LABELS = {
  meta_assunto: 'Meta do assunto',
  ixc: 'data_prazo_limite',
  sem_prazo: 'Sem prazo',
};

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isAbortError = (error) => (
  error?.name === 'AbortError'
  || error?.name === 'CanceledError'
  || error?.code === 'ERR_CANCELED'
);

const isIxcUnavailable = (error) => error?.response?.data?.code === 'IXC_UNAVAILABLE';

/**
 * Distância entre o fechamento e o prazo, escrita por extenso.
 *
 * O SQL entrega positivo para atraso e negativo para folga, mas o sinal sozinho obriga o leitor a
 * decorar a convenção — e um número negativo que significa "bom" se lê errado. O rótulo diz qual é.
 */
const formatDesvio = (horas) => {
  if (horas === null || horas === undefined) {
    return '—';
  }

  const absoluto = Math.abs(horas);

  if (Math.round(absoluto * 60) === 0) {
    return 'no limite';
  }

  return `${formatInfraDurationHours(absoluto)} de ${horas > 0 ? 'atraso' : 'folga'}`;
};

const buildLinha = (ordem) => ({
  ...ordem,
  abertura: ordem.data_abertura ? formatInfraDateTime(ordem.data_abertura) : '—',
  fechamento: ordem.data_fechamento ? formatInfraDateTime(ordem.data_fechamento) : '—',
  prazo: ordem.prazo_efetivo ? formatInfraDateTime(ordem.prazo_efetivo) : '—',
  origem: ordem.prazo_origem ? (ORIGEM_LABELS[ordem.prazo_origem] || ordem.prazo_origem) : '',
  desvio: formatDesvio(ordem.horas_desvio),
  execucao: ordem.horas_execucao === null || ordem.horas_execucao === undefined
    ? '—'
    : formatInfraDurationHours(ordem.horas_execucao),
});

const OsSlaDetalheModal = ({ situacao, range, onClose, isMobile = false }) => {
  const [detalhe, setDetalhe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [unavailable, setUnavailable] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [busca, setBusca] = useState('');
  const requestIdRef = useRef(0);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  const start = range?.start;
  const end = range?.end;
  const retry = useCallback(() => setReloadToken((current) => current + 1), []);

  useEffect(() => {
    setBusca('');
  }, [situacao]);

  useEffect(() => {
    if (!situacao || !start || !end) {
      return undefined;
    }

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const carregar = async () => {
      setLoading(true);
      setErrorMessage('');
      setUnavailable(false);

      try {
        const result = await osMetricsService.buscarDetalheSla(
          { data_inicio: start, data_fim: end, situacao },
          controller.signal,
        );

        if (requestId !== requestIdRef.current) {
          return;
        }

        setDetalhe(result);
      } catch (error) {
        if (isAbortError(error) || requestId !== requestIdRef.current) {
          return;
        }

        setDetalhe(null);
        if (isIxcUnavailable(error)) {
          setUnavailable(true);
          setErrorMessage(error?.response?.data?.message
            || 'A leitura direta de métricas de OS ainda não está habilitada para esta empresa.');
        } else {
          setErrorMessage(error?.response?.data?.message
            || error?.message
            || 'Não foi possível carregar as ordens desta situação.');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    carregar();

    return () => controller.abort();
  }, [end, reloadToken, situacao, start]);

  // Trava a rolagem de fundo, devolve o foco ao fechar e mantém Tab dentro do modal.
  useEffect(() => {
    if (!situacao || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = 'hidden';

    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return;
      }

      const focusables = panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (!first || !last) {
        event.preventDefault();
        return;
      }

      if (!panelRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [onClose, situacao]);

  const linhas = useMemo(
    () => (detalhe?.ordens || []).map(buildLinha),
    [detalhe],
  );

  const buscaNormalizada = busca.trim().toLowerCase();

  const linhasFiltradas = useMemo(() => {
    if (!buscaNormalizada) {
      return linhas;
    }

    return linhas.filter((linha) => [
      linha.id_os,
      linha.assunto,
      linha.cidade_nome,
      linha.funcionario,
      linha.abertura,
      linha.fechamento,
      linha.prazo,
      linha.origem,
      linha.desvio,
      linha.execucao,
    ].some((valor) => String(valor ?? '').toLowerCase().includes(buscaNormalizada)));
  }, [buscaNormalizada, linhas]);

  if (!situacao) {
    return null;
  }

  const rotulo = SITUACAO_LABELS[situacao] || situacao;

  return (
    <>
      <div
        className="fixed inset-0 z-[119] bg-slate-950/78"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        data-testid="os-sla-detalhe-modal"
        className="fixed inset-x-4 top-[calc(var(--header-shell-height)+12px)] bottom-4 z-[120] flex flex-col overflow-hidden rounded-[28px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-4 shadow-[0_28px_80px_rgba(2,6,23,0.45)] sm:inset-x-8 sm:top-[calc(var(--header-shell-height)+16px)] sm:bottom-6 sm:p-5 lg:inset-x-12"
      >
        <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h3 id={TITLE_ID} className="text-lg font-semibold text-[color:var(--text-primary)]">
              {`OS ${rotulo}`}
            </h3>
            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
              {detalhe
                ? `${linhasFiltradas.length.toLocaleString('pt-BR')} de ${detalhe.total.toLocaleString('pt-BR')} registros visíveis`
                : 'Carregando ordens…'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-full lg:max-w-sm">
              <label className="sr-only" htmlFor="os-sla-detalhe-busca">Pesquisar nas ordens</label>
              <input
                id="os-sla-detalhe-busca"
                type="search"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                placeholder="Pesquisar por OS, assunto, cidade, técnico..."
                className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] outline-none transition focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)]/20"
              />
            </div>
            {loading ? <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-[color:var(--text-muted)]" /> : null}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Fechar detalhamento de SLA"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {detalhe?.truncado ? (
          <div className="mb-3 flex items-start gap-2 rounded-[12px] border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[11px] leading-4 text-[color:var(--text-muted)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
              {`A lista foi limitada a ${detalhe.exibidas.toLocaleString('pt-BR')} de ${detalhe.total.toLocaleString('pt-BR')} OS. Reduza o período para ver o restante.`}
            </span>
          </div>
        ) : null}

        {errorMessage ? (
          <div
            className={`flex items-start gap-3 rounded-[12px] border px-3 py-2 text-xs leading-5 ${
              unavailable
                ? 'border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/55 text-[color:var(--text-muted)]'
                : 'border-amber-500/25 bg-amber-500/10 text-[color:var(--text-muted)]'
            }`}
          >
            {unavailable
              ? <Info className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--accent)]/85" />
              : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />}
            <span className="min-w-0 flex-1">{errorMessage}</span>
            <button
              type="button"
              onClick={retry}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[color:var(--header-control-border-subtle)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:border-[color:var(--accent)]/45"
            >
              <RefreshCw className="h-3 w-3" />
              Tentar novamente
            </button>
          </div>
        ) : null}

        {!errorMessage && isMobile ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
            {linhasFiltradas.map((linha) => (
              <article
                key={linha.id_os}
                className="rounded-[18px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--text-muted)]">
                      OS #{linha.id_os}
                    </p>
                    <h4 className="mt-1 text-sm font-semibold leading-5 text-[color:var(--text-primary)]">
                      {linha.assunto || '—'}
                    </h4>
                  </div>
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    linha.horas_desvio > 0
                      ? 'border-rose-500/30 text-rose-500'
                      : 'border-[color:var(--header-control-border-subtle)] text-[color:var(--text-primary)]'
                  }`}
                  >
                    {linha.desvio}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div><span className="text-[color:var(--text-muted)]">Cidade:</span> <span className="text-[color:var(--text-primary)]">{linha.cidade_nome || '—'}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Técnico:</span> <span className="text-[color:var(--text-primary)]">{linha.funcionario || 'Não informado'}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Abertura:</span> <span className="text-[color:var(--text-primary)]">{linha.abertura}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Fechamento:</span> <span className="text-[color:var(--text-primary)]">{linha.fechamento}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Prazo:</span> <span className="text-[color:var(--text-primary)]">{linha.prazo}</span></div>
                  <div><span className="text-[color:var(--text-muted)]">Execução:</span> <span className="text-[color:var(--text-primary)]">{linha.execucao}</span></div>
                </div>

                {linha.origem ? (
                  <p className="mt-2 text-[11px] text-[color:var(--text-muted)]">
                    {`Prazo por ${linha.origem}${linha.meta_horas ? ` · ${linha.meta_horas.toLocaleString('pt-BR')}h` : ''}`}
                  </p>
                ) : null}
              </article>
            ))}
            {linhasFiltradas.length === 0 && !loading ? (
              <div className="rounded-[18px] border border-dashed border-[color:var(--border)] px-4 py-8 text-center text-sm text-[color:var(--text-muted)]">
                {buscaNormalizada
                  ? 'Nenhum registro encontrado para a busca informada.'
                  : 'Nenhuma OS nesta situação no período.'}
              </div>
            ) : null}
          </div>
        ) : null}

        {!errorMessage && !isMobile ? (
          <div
            data-testid="os-sla-detalhe-scroll"
            className="min-h-0 flex-1 overflow-auto rounded-xl border border-[color:var(--border)]/60"
          >
            <table className="min-w-[980px] w-full text-xs">
              <caption className="sr-only">{`Ordens de serviço na situação ${rotulo}`}</caption>
              <thead className="sticky top-0 z-10 bg-[color:var(--surface-strong)]">
                <tr className="border-b border-[color:var(--border)] text-left text-[color:var(--text-muted)]">
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">OS</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Assunto</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Cidade</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Técnico</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Abertura</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Fechamento</th>
                  <th scope="col" className="px-2 py-2 font-semibold uppercase tracking-[0.06em]">Prazo</th>
                  <th scope="col" className="px-2 py-2 text-right font-semibold uppercase tracking-[0.06em]">Folga / Atraso</th>
                  <th scope="col" className="px-2 py-2 text-right font-semibold uppercase tracking-[0.06em]">Execução</th>
                </tr>
              </thead>
              <tbody>
                {linhasFiltradas.map((linha) => (
                  <tr key={linha.id_os} className="border-b border-[color:var(--border)]/40 hover:bg-[color:var(--surface-muted)]/40">
                    <td className="px-2 py-2 font-medium text-[color:var(--text-primary)]">{linha.id_os}</td>
                    <td className="px-2 py-2">{linha.assunto || '—'}</td>
                    <td className="px-2 py-2">{linha.cidade_nome || '—'}</td>
                    <td className="px-2 py-2">{linha.funcionario || 'Não informado'}</td>
                    <td className="px-2 py-2">{linha.abertura}</td>
                    <td className="px-2 py-2">{linha.fechamento}</td>
                    <td className="px-2 py-2">
                      {linha.prazo}
                      {linha.origem ? (
                        <span className="block text-[10px] text-[color:var(--text-muted)]">
                          {linha.origem}
                          {linha.meta_horas ? ` · ${linha.meta_horas.toLocaleString('pt-BR')}h` : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className={`px-2 py-2 text-right font-medium ${
                      linha.horas_desvio > 0 ? 'text-rose-500' : 'text-[color:var(--text-primary)]'
                    }`}
                    >
                      {linha.desvio}
                    </td>
                    <td className="px-2 py-2 text-right">{linha.execucao}</td>
                  </tr>
                ))}
                {linhasFiltradas.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={9} className="px-2 py-8 text-center text-sm text-[color:var(--text-muted)]">
                      {buscaNormalizada
                        ? 'Nenhum registro encontrado para a busca informada.'
                        : 'Nenhuma OS nesta situação no período.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default OsSlaDetalheModal;
